import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Phone, AlertTriangle, CheckCircle2, RefreshCw,
  ExternalLink, Copy, Check, RadioTower, History, ServerCrash,
} from "lucide-react";
import { API } from "../../../App";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";

/**
 * Twilio Diagnostics — single glance-and-go view of voice integration health.
 *
 * What this panel surfaces (one screen, three columns / stacks on mobile):
 *
 *   1. Health header  — `/api/twilio-health` (no auth required) reporting
 *      WEBHOOK_BASE_URL, the live TwiML App URLs Twilio has stored, and a
 *      `drift` flag. If `drift=true`, a one-click "Force Sync" button hits
 *      `POST /api/admin/twilio/sync-webhook` and corrects it.
 *
 *   2. Recent Call Failures  — `/api/admin/twilio/call-failures` — the
 *      in-house collection that captures every Twilio status callback marked
 *      failed/busy/no-answer/canceled. Newest-first, color-coded by error
 *      code. This is the in-house mirror of "what your customers experienced".
 *
 *   3. Twilio Monitor Errors  — `/api/admin/twilio/recent-errors` — pulls the
 *      LIVE alert feed from Twilio's own Monitor API (what you'd see in
 *      Twilio Console → Monitor → Errors). Shows the EXACT URL Twilio was
 *      hitting at moment of failure, the carrier error code, and a deep link
 *      back to the Twilio Console for full forensics.
 *
 * Color-coding scheme (consistent across both error feeds):
 *   • 11xxx (URL retrieval)        → red    — webhook URL is broken / dead
 *   • 13xxx (TwiML response error) → amber  — your TwiML was invalid
 *   • 15xxx (account warnings)     → amber  — usually retry/throttle
 *   • 20xxx (REST API errors)      → blue   — auth or request shape issue
 *   • Other / N/A                  → muted  — unclassified
 */

const ERROR_CODE_DOCS_URL = (code) =>
  code ? `https://www.twilio.com/docs/api/errors/${code}` : "";
const TWILIO_CONSOLE_ERRORS_URL = "https://console.twilio.com/us1/monitor/logs/alerts";

function classifyErrorCode(code) {
  const c = String(code || "").trim();
  if (!c) return { tone: "muted", label: "—" };
  const n = parseInt(c, 10);
  if (!Number.isFinite(n)) return { tone: "muted", label: c };
  if (n >= 11000 && n < 12000) return { tone: "red", label: c };
  if (n >= 13000 && n < 14000) return { tone: "amber", label: c };
  if (n >= 15000 && n < 16000) return { tone: "amber", label: c };
  if (n >= 20000 && n < 21000) return { tone: "blue", label: c };
  if (n === 0) return { tone: "muted", label: "—" };
  return { tone: "muted", label: c };
}

function toneClasses(tone) {
  switch (tone) {
    case "red":
      return "text-red-500 bg-red-500/10 border-red-500/30";
    case "amber":
      return "text-amber-500 bg-amber-500/10 border-amber-500/30";
    case "blue":
      return "text-blue-500 bg-blue-500/10 border-blue-500/30";
    default:
      return "text-muted-foreground bg-muted/20 border-border/40";
  }
}

const ErrorCodeBadge = ({ code }) => {
  const { tone, label } = classifyErrorCode(code);
  const url = ERROR_CODE_DOCS_URL(code);
  const cls = `inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-mono ${toneClasses(tone)}`;
  if (url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className={cls} title={`Twilio error ${code} docs`}>
        {label} <ExternalLink className="w-2.5 h-2.5" />
      </a>
    );
  }
  return <span className={cls}>{label}</span>;
};

const Stat = ({ label, value, tone = "muted" }) => (
  <div className={`rounded-lg border px-3 py-2 ${toneClasses(tone)}`}>
    <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
    <div className="text-lg font-semibold mt-0.5 font-mono leading-tight">{value}</div>
  </div>
);

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const TwilioDiagnosticsPanel = () => {
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);

  const [failures, setFailures] = useState([]);
  const [failuresLoading, setFailuresLoading] = useState(true);

  const [alerts, setAlerts] = useState([]);
  const [alertsStatus, setAlertsStatus] = useState("loading");
  const [alertsLoading, setAlertsLoading] = useState(true);

  const [syncing, setSyncing] = useState(false);
  const [copied, setCopied] = useState(null);

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    try {
      // /api/twilio-health is intentionally public so external monitors can
      // hit it; it's safe to call from the admin UI without auth wrapping.
      const r = await axios.get(`${API}/twilio-health`);
      setHealth(r.data);
    } catch (e) {
      setHealth({ ok: false, error: e?.message || "Health check failed" });
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const fetchFailures = useCallback(async () => {
    setFailuresLoading(true);
    try {
      const r = await axios.get(`${API}/admin/twilio/call-failures?limit=10`, { headers: getAuthHeaders() });
      setFailures(r.data?.failures || []);
    } catch (e) {
      // Non-critical — leave empty so the rest of the panel still renders.
      setFailures([]);
    } finally {
      setFailuresLoading(false);
    }
  }, []);

  const fetchAlerts = useCallback(async () => {
    setAlertsLoading(true);
    try {
      const r = await axios.get(`${API}/admin/twilio/recent-errors?limit=10`, { headers: getAuthHeaders() });
      setAlertsStatus(r.data?.status || "unknown");
      setAlerts(r.data?.alerts || []);
    } catch (e) {
      setAlertsStatus("fetch_error");
      setAlerts([]);
    } finally {
      setAlertsLoading(false);
    }
  }, []);

  const refreshAll = useCallback(() => {
    fetchHealth();
    fetchFailures();
    fetchAlerts();
  }, [fetchHealth, fetchFailures, fetchAlerts]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const handleForceSync = async () => {
    setSyncing(true);
    try {
      const r = await axios.post(`${API}/admin/twilio/sync-webhook`, {}, { headers: getAuthHeaders() });
      const status = r.data?.status;
      if (status === "synced") {
        toast.success("Twilio TwiML App URLs corrected");
      } else if (status === "ok") {
        toast.success("Twilio URLs already match — nothing to sync");
      } else if (status === "non_canonical") {
        toast.warning("This environment is not canonical — sync skipped (use --force)");
      } else if (status === "missing_env") {
        toast.error("Twilio creds missing in backend/.env");
      } else {
        toast.message(`Sync result: ${status}`);
      }
      // Re-pull health to reflect the new state.
      fetchHealth();
    } catch (e) {
      toast.error(`Sync failed: ${e?.response?.data?.detail || e?.message || "unknown"}`);
    } finally {
      setSyncing(false);
    }
  };

  const copyToClipboard = async (label, text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(label);
      toast.success(`Copied ${label}`);
      setTimeout(() => setCopied(null), 1200);
    } catch {
      toast.error("Could not copy");
    }
  };

  const ok = health?.ok === true;
  const drift = health?.twiml_app?.drift === true;

  // Aggregate stats — top of the panel for at-a-glance status.
  const failureCount24h = failures.filter((f) => {
    if (!f.created_at) return false;
    const ageMs = Date.now() - new Date(f.created_at).getTime();
    return ageMs < 24 * 3600 * 1000;
  }).length;
  const alertCount24h = alerts.filter((a) => {
    if (!a.date_created) return false;
    return Date.now() - new Date(a.date_created).getTime() < 24 * 3600 * 1000;
  }).length;

  return (
    <Card className="glass-card border-theme overflow-hidden" data-testid="twilio-diag-panel">
      <div className="px-4 py-2.5 border-b border-border/50 flex items-center gap-2 bg-blue-500/10">
        <Phone className="w-4 h-4 text-blue-500" />
        <h3 className="text-sm font-semibold text-foreground font-['Manrope']">Twilio Diagnostics</h3>
        <Badge variant="outline" className="ml-2 text-[10px]">live</Badge>
        {!healthLoading && (
          <Badge
            variant="outline"
            className={`text-[10px] ${ok ? "border-emerald-500/40 text-emerald-500" : "border-red-500/40 text-red-500"}`}
            data-testid="twilio-health-badge"
          >
            {ok ? "healthy" : "needs attention"}
          </Badge>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={refreshAll}
            disabled={healthLoading || failuresLoading || alertsLoading}
            data-testid="twilio-diag-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${(healthLoading || failuresLoading || alertsLoading) ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <CardContent className="p-4 space-y-4">
        {/* ─────────────────── HEALTH HEADER ─────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Stat label="Health" value={ok ? "OK" : "Drift"} tone={ok ? "muted" : "red"} />
          <Stat label="Webhook drift" value={drift ? "YES" : "NO"} tone={drift ? "amber" : "muted"} />
          <Stat label="Failures (24h)" value={failureCount24h} tone={failureCount24h > 0 ? "red" : "muted"} />
          <Stat label="Twilio alerts (24h)" value={alertCount24h} tone={alertCount24h > 0 ? "amber" : "muted"} />
        </div>

        {/* ─────────────────── HEALTH DETAILS ─────────────────── */}
        <div className={`rounded-lg border p-3 ${ok ? "border-border/40" : "border-amber-500/40 bg-amber-500/5"}`}>
          <div className="flex items-center gap-2 mb-2">
            <RadioTower className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-semibold text-foreground">Webhook configuration</span>
            {drift && (
              <Button
                size="sm"
                variant="default"
                onClick={handleForceSync}
                disabled={syncing}
                className="ml-auto"
                data-testid="force-sync-btn"
              >
                {syncing ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />}
                Force Sync
              </Button>
            )}
          </div>
          {healthLoading ? (
            <div className="text-xs text-muted-foreground">Checking…</div>
          ) : (
            <div className="space-y-1.5 text-xs font-mono">
              <UrlRow label="WEBHOOK_BASE_URL" value={health?.webhook_base_url || "(not set)"} onCopy={copyToClipboard} copied={copied === "WEBHOOK_BASE_URL"} />
              <UrlRow label="voice_url" value={health?.expected?.voice_url} onCopy={copyToClipboard} copied={copied === "voice_url"} />
              <UrlRow label="voice_fallback_url" value={health?.expected?.voice_fallback_url} onCopy={copyToClipboard} copied={copied === "voice_fallback_url"} />
              <UrlRow label="status_callback" value={health?.expected?.status_callback} onCopy={copyToClipboard} copied={copied === "status_callback"} />
              <UrlRow label="incoming_call_url" value={health?.expected?.incoming_call_url} onCopy={copyToClipboard} copied={copied === "incoming_call_url"} />
              <UrlRow label="self_test_url" value={health?.self_test_url} onCopy={copyToClipboard} copied={copied === "self_test_url"} />
              <div className="pt-1 text-[10px] text-muted-foreground">
                TwiML App SID: <span className="text-foreground">{health?.twiml_app?.sid || "(not set)"}</span>
                {health?.twiml_app?.status && (
                  <Badge variant="outline" className="ml-2 text-[10px]">{health.twiml_app.status}</Badge>
                )}
              </div>
              {drift && Array.isArray(health?.twiml_app?.drift_fields) && health.twiml_app.drift_fields.length > 0 && (
                <div className="text-[11px] text-amber-500 mt-1.5">
                  Drift detected on: {health.twiml_app.drift_fields.join(", ")}. Click Force Sync to correct.
                </div>
              )}
            </div>
          )}
        </div>

        {/* ─────────────── TWO-COLUMN: FAILURES + ALERTS ─────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {/* In-house call_failures */}
          <div className="rounded-lg border border-border/40 overflow-hidden">
            <div className="px-3 py-2 border-b border-border/40 flex items-center gap-2 bg-muted/20">
              <ServerCrash className="w-4 h-4 text-red-500" />
              <span className="text-xs font-semibold text-foreground">Recent Call Failures (in-house)</span>
              <Badge variant="outline" className="text-[10px] ml-auto">{failures.length}</Badge>
            </div>
            <div className="max-h-[28rem] overflow-y-auto">
              {failuresLoading ? (
                <div className="p-4 text-xs text-muted-foreground text-center">Loading failures…</div>
              ) : failures.length === 0 ? (
                <div className="p-6 text-xs text-muted-foreground text-center flex flex-col items-center gap-1.5">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  No call failures recorded.
                </div>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full text-[11px]" data-testid="failures-table">
                  <thead className="bg-muted/10 sticky top-0">
                    <tr className="text-left text-muted-foreground">
                      <th className="px-3 py-1.5 font-medium">When</th>
                      <th className="px-3 py-1.5 font-medium">Code</th>
                      <th className="px-3 py-1.5 font-medium">Status</th>
                      <th className="px-3 py-1.5 font-medium">From → To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {failures.map((f, i) => (
                      <tr key={f.call_sid || i} className="border-t border-border/30 hover:bg-muted/10 align-top" data-testid={`failure-row-${i}`}>
                        <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap" title={f.created_at}>
                          {f.created_at ? formatDistanceToNow(new Date(f.created_at), { addSuffix: true }) : "—"}
                        </td>
                        <td className="px-3 py-1.5"><ErrorCodeBadge code={f.error_code} /></td>
                        <td className="px-3 py-1.5">
                          <Badge variant="outline" className="text-[10px] capitalize">{f.status || "—"}</Badge>
                        </td>
                        <td className="px-3 py-1.5 font-mono text-foreground">
                          <div className="truncate max-w-[12rem]" title={`${f.from_number || "?"} → ${f.to_number || "?"}`}>
                            {f.from_number || "?"} → {f.to_number || "?"}
                          </div>
                          {f.error_message && (
                            <div className="text-[10px] text-muted-foreground truncate max-w-[14rem]" title={f.error_message}>
                              {f.error_message}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>

          {/* Live Twilio Monitor alerts */}
          <div className="rounded-lg border border-border/40 overflow-hidden">
            <div className="px-3 py-2 border-b border-border/40 flex items-center gap-2 bg-muted/20">
              <History className="w-4 h-4 text-amber-500" />
              <span className="text-xs font-semibold text-foreground">Twilio Monitor Alerts (live)</span>
              <Badge variant="outline" className="text-[10px]">{alerts.length}</Badge>
              <a
                href={TWILIO_CONSOLE_ERRORS_URL}
                target="_blank"
                rel="noreferrer"
                className="ml-auto text-[10px] text-blue-500 hover:underline inline-flex items-center gap-0.5"
                title="Open Twilio Console"
              >
                Console <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div className="max-h-[28rem] overflow-y-auto">
              {alertsLoading ? (
                <div className="p-4 text-xs text-muted-foreground text-center">Loading Twilio alerts…</div>
              ) : alertsStatus === "missing_env" ? (
                <div className="p-6 text-xs text-amber-500 text-center">
                  Twilio credentials missing in backend/.env — can't query Monitor API.
                </div>
              ) : alertsStatus === "fetch_error" ? (
                <div className="p-6 text-xs text-red-500 text-center flex flex-col items-center gap-1.5">
                  <AlertTriangle className="w-5 h-5" />
                  Could not reach Twilio Monitor API.
                </div>
              ) : alerts.length === 0 ? (
                <div className="p-6 text-xs text-muted-foreground text-center flex flex-col items-center gap-1.5">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  No recent Twilio alerts.
                </div>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full text-[11px]" data-testid="alerts-table">
                  <thead className="bg-muted/10 sticky top-0">
                    <tr className="text-left text-muted-foreground">
                      <th className="px-3 py-1.5 font-medium">When</th>
                      <th className="px-3 py-1.5 font-medium">Code</th>
                      <th className="px-3 py-1.5 font-medium">Level</th>
                      <th className="px-3 py-1.5 font-medium">URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alerts.map((a, i) => (
                      <tr key={a.sid || i} className="border-t border-border/30 hover:bg-muted/10 align-top" data-testid={`alert-row-${i}`}>
                        <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap" title={a.date_created}>
                          {a.date_created ? formatDistanceToNow(new Date(a.date_created), { addSuffix: true }) : "—"}
                        </td>
                        <td className="px-3 py-1.5"><ErrorCodeBadge code={a.error_code} /></td>
                        <td className="px-3 py-1.5">
                          <Badge
                            variant="outline"
                            className={`text-[10px] capitalize ${a.log_level === "error" ? "border-red-500/40 text-red-500" : "border-amber-500/40 text-amber-500"}`}
                          >
                            {a.log_level || "—"}
                          </Badge>
                        </td>
                        <td className="px-3 py-1.5 font-mono">
                          <div className="text-foreground truncate max-w-[16rem]" title={a.request_url}>
                            {a.request_url || "—"}
                          </div>
                          {a.resource_sid && (
                            <div className="text-[10px] text-muted-foreground">{a.resource_sid}</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const UrlRow = ({ label, value, onCopy, copied }) => (
  <div className="flex items-center gap-2 group">
    <span className="text-[10px] uppercase tracking-wide text-muted-foreground w-32 flex-shrink-0">{label}</span>
    <span className="text-foreground truncate flex-1" title={value}>{value || "—"}</span>
    {value && (
      <button
        type="button"
        onClick={() => onCopy(label, value)}
        className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
        title={`Copy ${label}`}
      >
        {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
      </button>
    )}
  </div>
);

export default TwilioDiagnosticsPanel;
